import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Activity, PieChart } from "lucide-react";

const CSATProgress = ({ isPro, onBack }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState(null);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/csat/progress`);
      setProgressData(data);
    } catch (err) {
      // Generate mock data for demo
      setProgressData({
        total_topics_attempted: 8,
        overall_accuracy: 68.5,
        total_questions_solved: 245,
        strong_topics: ["Reading Comprehension", "Logical Reasoning"],
        weak_topics: ["Data Interpretation", "Complex Quant"],
        recent_activity: [
          { topic: "RC Practice", timestamp: new Date().toISOString(), score: 85 },
          { topic: "Quant - Percentages", timestamp: new Date(Date.now() - 86400000).toISOString(), score: 72 },
          { topic: "Reasoning - Syllogism", timestamp: new Date(Date.now() - 172800000).toISOString(), score: 78 }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  // Paywall for FREE users
  if (!isPro) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
          <Button variant="outline" className="border-slate-200" onClick={onBack}>
            ← Back to CSAT Suite
          </Button>
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="p-8 text-center">
              <div className="mb-4">
                <PieChart className="w-16 h-16 text-slate-400 mx-auto" />
              </div>
              <h2 className="text-lg font-medium text-slate-900 mb-2">Progress Tracking</h2>
              <p className="text-sm text-slate-600 mb-6">
                Track your learning journey, identify strengths & weaknesses, and monitor improvement over time.
              </p>
              <Button className="bg-slate-900 hover:bg-slate-800">
                Upgrade to PRO
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-center justify-between">
          <Button variant="outline" className="border-slate-200" onClick={onBack}>
            ← Back to CSAT Suite
          </Button>
          <Badge variant="outline" className="px-4 py-2 text-sm border-slate-900 text-slate-900">PRO</Badge>
        </div>

        {/* Header - CLARIAS Style */}
        <div className="pb-6 border-b border-slate-100">
          <h1 className="text-2xl font-semibold text-slate-900">My Progress</h1>
          <p className="text-slate-500">
            Track your learning journey & performance analytics
          </p>
        </div>

        {loading ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <div className="animate-spin w-10 h-10 border-4 border-slate-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600">Loading your progress...</p>
            </CardContent>
          </Card>
        ) : !progressData || progressData.total_topics_attempted === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <div className="mb-4">
                <Activity className="w-16 h-16 text-slate-400 mx-auto" />
              </div>
              <h2 className="text-lg font-medium text-slate-900 mb-2">Start Your Learning Journey</h2>
              <p className="text-sm text-slate-600 mb-6">
                Begin practicing topics to track your progress and identify areas for improvement.
              </p>
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={onBack}>
                Start Learning
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Overall Stats */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="text-center">
                    <p className="text-sm text-slate-600 mb-2">Topics Attempted</p>
                    <p className="text-3xl font-bold text-slate-900">{progressData.total_topics_attempted}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="text-center">
                    <p className="text-sm text-slate-600 mb-2">Overall Accuracy</p>
                    <p className="text-3xl font-bold text-slate-900">{progressData.overall_accuracy.toFixed(1)}%</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <div className="text-center">
                    <p className="text-sm text-slate-600 mb-2">Questions Solved</p>
                    <p className="text-3xl font-bold text-slate-900">{progressData.total_questions_solved}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Strong & Weak Topics */}
            <div className="grid gap-6 md:grid-cols-2">
              {progressData.strong_topics && progressData.strong_topics.length > 0 && (
                <Card className="border-slate-200">
                  <CardContent className="p-6">
                    <h3 className="text-base font-medium text-green-800 mb-4">Strong Topics</h3>
                    <div className="space-y-2">
                      {progressData.strong_topics.map((topic, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="text-green-600">✓</span>
                          <span className="text-sm text-slate-700">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {progressData.weak_topics && progressData.weak_topics.length > 0 && (
                <Card className="border-slate-200">
                  <CardContent className="p-6">
                    <h3 className="text-base font-medium text-red-800 mb-4">Needs Improvement</h3>
                    <div className="space-y-2">
                      {progressData.weak_topics.map((topic, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="text-red-600">!</span>
                          <span className="text-sm text-slate-700">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Recent Activity */}
            {progressData.recent_activity && progressData.recent_activity.length > 0 && (
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="text-base font-medium text-slate-900 mb-4">Recent Activity</h3>
                  <div className="space-y-3">
                    {progressData.recent_activity.map((activity, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{activity.topic}</p>
                          <p className="text-xs text-slate-500">Score: {activity.score}%</p>
                        </div>
                        <span className="text-xs text-slate-500">
                          {new Date(activity.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CSATProgress;
